/**
 * PushManager - Verwaltet Push-Nachrichten und Service Worker
 */

class PushManager {
  constructor() {
    this.registration = null;
    this.subscription = null;
    this.apiUrl = process.env.REACT_APP_API_URL;
  }

  /**
   * Service Worker abrufen (wird von CRA registriert)
   */
  async registerServiceWorker() {
    if ('serviceWorker' in navigator) {
      try {
        // Wir verlassen uns auf serviceWorkerRegistration.js von CRA,
        // welches /service-worker.js registriert, und warten nur bis er bereit ist.
        this.registration = await navigator.serviceWorker.ready;
        console.log('PushManager nutzt aktiven Service Worker:', this.registration);
        return true;
      } catch (error) {
        console.error('Fehler beim Abrufen des aktiven Service Workers:', error);
        return false;
      }
    }
    return false;
  }

  /**
   * Push-Berechtigung anfordern
   */
  async requestPermission() {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  /**
   * Push-Subscription erstellen
   */
  async subscribeToPush() {
    if (!this.registration) {
      console.error('Service Worker not registered');
      return null;
    }

    try {
      // VAPID Public Key vom Server laden
      const vapidPublicKey = await this.loadVAPIDPublicKey();
      if (!vapidPublicKey) {
        console.error('Failed to load VAPID public key');
        return null;
      }

      this.subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey)
      });

      console.log('Push subscription created:', this.subscription);
      return this.subscription;
    } catch (error) {
      console.error('Failed to subscribe to push:', error);
      return null;
    }
  }

  /**
   * Push-Token beim Server registrieren
   */
  async registerTokenWithServer(subscription, deviceType = 'web') {
    if (!subscription) return false;

    try {
      const token = JSON.stringify(subscription);

      const response = await fetch(`${this.apiUrl}/push/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          token: token,
          deviceType: deviceType
        })
      });

      const result = await response.json();
      return result.success;
    } catch (error) {
      console.error('Failed to register token with server:', error);
      return false;
    }
  }

  /**
   * Vollständige Push-Setup durchführen
   */
  async setupPushNotifications() {
    // 1. Service Worker registrieren
    const swRegistered = await this.registerServiceWorker();
    if (!swRegistered) {
      console.warn('Service Worker registration failed');
      return false;
    }

    // 2. Berechtigung anfordern
    const permissionGranted = await this.requestPermission();
    if (!permissionGranted) {
      console.warn('Push permission denied');
      return false;
    }

    // 3. Subscription erstellen
    const subscription = await this.subscribeToPush();
    if (!subscription) {
      console.warn('Push subscription failed');
      return false;
    }

    // 4. Token beim Server registrieren
    const registered = await this.registerTokenWithServer(subscription);
    if (!registered) {
      console.warn('Token registration with server failed');
      return false;
    }

    console.log('Push notifications setup complete');
    return true;
  }

  /**
   * Push-Subscription entfernen
   */
  async unsubscribeFromPush() {
    if (this.subscription) {
      try {
        await this.subscription.unsubscribe();
        this.subscription = null;

        // Token beim Server entfernen
        await this.unregisterTokenWithServer();

        console.log('Successfully unsubscribed from push notifications');
        return true;
      } catch (error) {
        console.error('Failed to unsubscribe:', error);
        return false;
      }
    }
    return false;
  }

  /**
   * Token beim Server entfernen
   */
  async unregisterTokenWithServer() {
    if (!this.subscription) return false;

    try {
      const token = JSON.stringify(this.subscription);

      const response = await fetch(`${this.apiUrl}/push/unregister`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          token: token
        })
      });

      const result = await response.json();
      return result.success;
    } catch (error) {
      console.error('Failed to unregister token:', error);
      return false;
    }
  }

  /**
   * Prüfen ob Push-Berechtigung erteilt wurde
   */
  hasPermission() {
    return Notification.permission === 'granted';
  }

  /**
   * Update-Benachrichtigung anzeigen
   */
  showUpdateNotification() {
    if (this.registration && 'showNotification' in this.registration) {
      this.registration.showNotification('SgeM Update verfügbar', {
        body: 'Eine neue Version der App ist verfügbar. Aktualisiere die Seite.',
        icon: '/icon-192x192.png',
        badge: '/icon-192x192.png',
        tag: 'update'
      });
    }
  }

  /**
   * VAPID Key von Base64 zu Uint8Array konvertieren
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

// Singleton-Instanz
const pushManager = new PushManager();

export default pushManager;